import { StaticImage } from "gatsby-plugin-image";
import React, { useState } from "react";
import { Flex, Container, Box } from "theme-ui";
import { Icon } from "../../optimizely-components/Utilities/Icons/Icon";
import NavigationDots from "../Carousel/NavigationDots";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { easeInOutExpo } from "../../utils/animations";
import SliderNavigation from "./SliderNavigation";

interface Props {}

const loanInfo = [
  {
    title: "Line of Credit",
    benefits: [
      "Our Most Popular Product.",
      "Continuous borrowing flexibility.",
      "You choose how much to borrow, then only pay on what you use.",
      "With a higher credit limit than a Payday Loan and the benefit of using it when you need it, our Line of Credit is a top choice among our Customers.",
    ],
  },
  {
    title: "Auto Equity Loan",
    benefits: [
      "Would benefit from a secured loan.",
      "Could be approved for more money than an unsecured loan.",
      "Keep your vehicle and ownership documentation.",
      "You could build your credit<sup>†</sup>.",
    ],
  },
  {
    title: "Payday Loan",
    benefits: [
      "Need funds fast!",
      "Only need a smaller loan for a shorter time.",
      "Don’t need collateral or great credit as the ability to repay is most important.",
      "Easily apply online, quickly and conveniently.",
    ],
  },
];

const images = [
  <StaticImage
    src="../../images/home/LineOfCreditImage.jpg"
    alt="Cooking out"
  />,
  <StaticImage
    src="../../images/home/AELBannerCarousel.jpg"
    alt="Man in car"
  />,
  <StaticImage
    src="../../images/home/PaydayBannerCarousel.jpg"
    alt="Man on phone"
  />,
];

const LoanProductsSlider = (props: Props) => {
  const [index, setIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: true,
      defaultAnimation: {
        easing: (t) => easeInOutExpo(t),
        duration: 500,
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
    },
    [
      // add plugins here
    ]
  );
  return (
    <Container
      sx={{
        maxWidth: 1329,
        p: 0,
        position: "relative",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        px: 3,
      }}
    >
      <div
        data-sal="slide-up"
        data-sal-delay="0"
        data-sal-duration="1000"
        data-sal-easing="easeOutQuart"
        sx={{
          position: "relative",
          maxWidth: ["402px", null, null, "100%"],
          width: "100%",
          p: 0,
          overflow: "hidden",
          borderRadius: "15px",
          height: ["100%", null, null, 445],
        }}
      >
        {/* Desktop */}
        <Container
          sx={{
            maxWidth: 1329,
            width: ["375px", null, null, "100%"],
            bg: "brand.darkNeutral",
            display: ["none", null, null, "flex"],
            color: "white",
            p: 0,
            height: "100%",
            position: "relative",
            flexDirection: ["column", null, null, "row"],
          }}
        >
          <div
            sx={{
              position: "relative",
              height: [287, null, null, 445],
              width: [375, 430, null, 670],
              overflow: "hidden",
            }}
          >
            <div
              sx={{
                transform: [
                  `translateX(-${index * 375}px)`,
                  `translateX(-${index * 430}px)`,
                  null,
                  `translateX(-${index * 670}px)`,
                ],
                transition: "transform 0.5s cubic-bezier(.64,-0.01,.37,.99)",
              }}
            >
              {images.map((image, i) => (
                <Box
                  key={i}
                  sx={{
                    position: "absolute",
                    width: [375, 430, null, 670],
                    height: [287, null, null, 445],
                    transform: [
                      `translateX(${i * 375}px)`,
                      `translateX(${i * 430}px)`,
                      null,
                      `translateX(${i * 670}px)`,
                    ],
                  }}
                >
                  {image}
                </Box>
              ))}
            </div>
          </div>
          <Flex
            sx={{
              width: [350, 430, null, 660],
              height: [350, null, null, 445],
              pl: [0, null, null, "90px"],
              px: [3, null, null, 0],
              overflow: "hidden",
            }}
          >
            <div
              sx={{
                height: ["100%"],
                transform: [
                  `translateX(-${index * 350}px)`,
                  `translateX(-${index * 430}px)`,
                  null,
                  `translateX(-${index * 660}px)`,
                ],
                transition: "transform 0.5s cubic-bezier(.64,-0.01,.37,.99)",
              }}
            >
              {loanInfo.map((loan, i) => (
                <div
                  key={i}
                  sx={{
                    position: "absolute",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: ["flex-start", null, null, "center"],
                    pl: [0, null, null, 5],
                    transform: [
                      `translateX(${i * 350}px)`,
                      `translateX(${i * 430}px)`,
                      null,
                      `translateX(${i * 660}px)`,
                    ],
                    width: [350, 430, null, 375],
                    height: ["100%", null, null, 445],
                  }}
                >
                  <h2
                    sx={{
                      variant: "text.h1HeaderRegular",
                      color: "white",
                      pb: 4,
                      fontSize: "32px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {loan.title}
                  </h2>
                  {loan.benefits.map((benefit, i) => (
                    <Flex
                      key={i}
                      sx={{ alignItems: ["center"], width: [340, 410], pb: 2 }}
                    >
                      <div
                        sx={{
                          mr: 3,
                        }}
                      >
                        <Icon
                          name={"check-mark-polka-dot"}
                          size={20}
                          alt="checkmark"
                          color="black"
                        />
                      </div>
                      <p dangerouslySetInnerHTML={{ __html: benefit }} />
                    </Flex>
                  ))}
                </div>
              ))}
            </div>
          </Flex>
        </Container>
        {/* Desktop */}
        {/* Mobile */}
        <div
          sx={{
            display: ["block", null, null, "none"],
          }}
        >
          <div ref={sliderRef} className="keen-slider">
            {images.map((image, i) => (
              <Flex
                key={i}
                className={`keen-slider__slide`}
                sx={{
                  flexDirection: "column",
                  bg: "brand.darkNeutral",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: ["100%"],
                    height: [287, null, null, 445],
                  }}
                >
                  {image}
                </Box>
                <Box
                  sx={{
                    bg: "brand.darkNeutral",
                    height: "100%",
                    p: 4,
                  }}
                >
                  <h2
                    sx={{
                      variant: "text.h1HeaderRegular",
                      color: "white",
                      pb: 4,
                      fontSize: "32px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {loanInfo[i].title}
                  </h2>
                  {loanInfo[i].benefits.map((benefit, j) => (
                    <Flex
                      key={j}
                      sx={{
                        alignItems: "center",
                        // justifyContent: "center",
                        pb: 2,
                      }}
                    >
                      <div
                        sx={{
                          mr: 3,
                        }}
                      >
                        <Icon
                          name={"check-mark-polka-dot"}
                          size={20}
                          alt="checkmark"
                          color="black"
                        />
                      </div>
                      <p
                        sx={{
                          color: "white",
                        }}
                        dangerouslySetInnerHTML={{ __html: benefit }}
                      />
                    </Flex>
                  ))}
                </Box>
              </Flex>
            ))}
          </div>
        </div>

        {/* Mobile */}
      </div>
      <div
        sx={{
          mt: 4,
          display: ["block", null, null, "none"],
        }}
      >
        {loaded && instanceRef.current && (
          <SliderNavigation
            instanceRef={instanceRef}
            currentSlide={currentSlide}
          />
        )}
      </div>
      <Box
        sx={{
          py: 4,
          zIndex: 5,
          bottom: [30, null, null, 0],
          display: ["none", null, null, "block"],
        }}
      >
        <NavigationDots arrows {...{ index, setIndex, slides: 3 }} />
      </Box>
    </Container>
  );
};

export default LoanProductsSlider;
